import { useRef, useState } from 'react'
import { useNavigation } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { captureException } from '@sentry/react-native'
import {
  HistoricalPaymentGroupOrder,
  OrderResponse,
  resolveHistoricalRetryPayment,
} from '@ca/common'
import { ErpOrderDetail } from '@ca/common/types'
import nanoid from '@ui/utils/nanoid'
import { useCurrencyDecimals } from 'hooks/useCurrency'
import { useToast } from '@ui/toasts/useToastManager'
import { trpc } from 'api'
import usePayments from '@order/hooks/usePayments'
import useCartState from '@order/hooks/useCartState'
import { resolveProvider } from '@order/hooks/usePaymentMethods'
import { initialOrderState, SavedOrder } from '@order/hooks/state/orderState'
import { createHistoricalPaymentNavigation } from '@shell/navigation/historicalPaymentNavigation'
import { getHistoricalPaymentGroupQuery } from './historicalPaymentGroup'

const paymentFailureMessages = {
  lookup_failed: 'groupError',
  inconsistent: 'groupError',
  incomplete: 'groupError',
  canceled: 'nothingToPay',
  unsupported_payment: 'groupError',
  active_payment: 'activePayment',
  nothing_to_pay: 'nothingToPay',
} as const

const createHistoricalPaymentOrder = (
  orders: HistoricalPaymentGroupOrder[],
  amount: number,
  invoiceId: number | null
): SavedOrder => {
  const erpOrders = orders.map((order) => ({
    id: order.id,
    name: order.ref,
    amount: order.amount,
  })) as OrderResponse[]

  return {
    id: nanoid(),
    order: { ...initialOrderState },
    discounts: [],
    deals: { base: undefined, extra: undefined },
    result: {
      orders: erpOrders,
      total: amount,
      isValid: true,
      invoice_id: invoiceId,
    },
  }
}

export const useRetryPayment = (data: ErpOrderDetail) => {
  const decimals = useCurrencyDecimals()
  const navigation = useNavigation('/')
  const { t } = useTranslation(['order'])
  const toast = useToast()
  const utils = trpc.useUtils()
  const createPayment = usePayments((s) => s.createPayment)
  const removeDraftPayment = usePayments((s) => s.removeDraftPayment)
  const clearCart = useCartState((s) => s.reset)
  const [isPending, setIsPending] = useState(false)
  const isResolving = useRef(false)

  const showMessage = (
    message: 'groupError' | 'activePayment' | 'nothingToPay' | 'startError'
  ) => {
    const key = `order:details.payment.${message}`
    toast({
      variant: message === 'nothingToPay' ? 'info' : 'error',
      title: t(`${key}.title`),
      text: t(`${key}.desc`),
    })
  }

  const fetchPaymentGroup = async () => {
    try {
      return await utils.order.history.fetch(
        getHistoricalPaymentGroupQuery(data),
        { staleTime: 0 }
      )
    } catch (error) {
      captureException(error, {
        tags: { location: 'historical-payment/lookup' },
      })
      showMessage('groupError')
      return null
    }
  }

  const handleUnavailablePayment = async (
    reason: keyof typeof paymentFailureMessages
  ) => {
    showMessage(paymentFailureMessages[reason])
    if (reason !== 'nothing_to_pay' && reason !== 'canceled') {
      return
    }

    const refreshes = await Promise.allSettled([
      utils.order.getById.invalidate({ id: data.id }),
      utils.order.history.invalidate(),
    ])
    refreshes.forEach((refresh) => {
      if (refresh.status !== 'rejected') {
        return
      }
      captureException(refresh.reason, {
        tags: { location: 'historical-payment/refresh' },
      })
    })
  }

  const startPayment = (
    orders: HistoricalPaymentGroupOrder[],
    amount: number
  ) => {
    let localId: string | undefined
    try {
      const sourceOrder = createHistoricalPaymentOrder(
        orders,
        amount,
        data.invoice_id ?? null
      )
      const method = 'terminal'
      localId = createPayment({
        method,
        orderIds: [sourceOrder.id],
        amount,
        provider: resolveProvider(method),
        sourceOrders: [sourceOrder],
      })
      const navigationState = navigation.getState()
      if (!navigationState) {
        throw new Error('Navigation state is unavailable')
      }
      const action = createHistoricalPaymentNavigation(
        navigationState,
        data.id,
        localId
      )
      clearCart()
      navigation.dispatch(action)
    } catch (error) {
      if (localId) {
        removeDraftPayment(localId)
      }
      captureException(error, {
        tags: { location: 'historical-payment/start' },
      })
      showMessage('startError')
    }
  }

  const retry = async () => {
    if (isResolving.current) {
      return
    }

    isResolving.current = true
    setIsPending(true)
    try {
      const page = await fetchPaymentGroup()
      if (!page) {
        return
      }

      const result = resolveHistoricalRetryPayment({
        selected: data,
        groupLookup: {
          status: 'ok',
          members: page.data,
          hasMore: page.meta.nextCursor != null,
        },
        decimals,
      })
      if (!result.ok) {
        await handleUnavailablePayment(result.reason)
        return
      }

      startPayment(result.orders, result.amount)
    } finally {
      isResolving.current = false
      setIsPending(false)
    }
  }

  return { retry, isPending }
}
